import React, { useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@apollo/client'
import dayjs from 'dayjs'
import { Alert, Button, InputNumber, Popconfirm, Select, Space, Table, Typography, message } from 'antd'
import { CheckOutlined, CloseOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'

import {
  DELETE_FULL_TRUCK_LOAD_RATE_MUTATION,
  FULL_TRUCK_LOAD_RATES_QUERY,
  UPSERT_FULL_TRUCK_LOAD_RATE_MUTATION,
  basketSizeDetailPath,
  formatMutationErrors,
  formatPercent
} from './graphql'

const DATE_TIME_FORMAT = 'DD/MM/YYYY HH:mm'

const PercentInput = props => (
  <InputNumber addonAfter='%' min={0} step={0.5} style={{ width: 140 }} {...props} />
)

const FullTruckLoadRatesTab = () => {
  const { t } = useTranslation('customerShippingRates')
  const location = useLocation()
  const highlightedProvinceId = new URLSearchParams(location.search).get('province')

  const [editingProvinceId, setEditingProvinceId] = useState(null)
  const [editingPercentage, setEditingPercentage] = useState(null)
  const [newProvinceId, setNewProvinceId] = useState(undefined)
  const [newPercentage, setNewPercentage] = useState(null)
  const [busyProvinceId, setBusyProvinceId] = useState(null)

  const { data, error, loading, refetch } = useQuery(FULL_TRUCK_LOAD_RATES_QUERY, {
    fetchPolicy: 'cache-and-network'
  })
  const [upsertRate] = useMutation(UPSERT_FULL_TRUCK_LOAD_RATE_MUTATION)
  const [deleteRate] = useMutation(DELETE_FULL_TRUCK_LOAD_RATE_MUTATION)

  const rates = (data && data.fullTruckLoadRates) || []
  const canEdit = Boolean(data && data.customerShippingRatePermissions.canEditFullTruckLoadRates)

  const provincesWithoutRate = useMemo(() => {
    const usedIds = new Set(rates.map(rate => rate.province.id))

    return ((data && data.provinces) || [])
      .filter(province => !usedIds.has(province.id))
      .sort((a, b) => a.name.localeCompare(b.name, 'th'))
  }, [data])

  const save = async (provinceId, percentage) => {
    if (percentage === null || percentage === undefined) {
      message.error(t('fullTruckLoad.percentageRequired'))
      return false
    }

    setBusyProvinceId(provinceId)
    try {
      const { data: result } = await upsertRate({ variables: { provinceId, percentage } })
      const payload = result.upsertFullTruckLoadRate
      if (payload.errors && payload.errors.length) {
        message.error(formatMutationErrors(payload.errors))
        return false
      }
      message.success(t('saved'))
      await refetch()
      return true
    } catch (mutationError) {
      message.error(mutationError.message)
      return false
    } finally {
      setBusyProvinceId(null)
    }
  }

  const handleSaveEdit = async () => {
    if (await save(editingProvinceId, editingPercentage)) setEditingProvinceId(null)
  }

  const handleAdd = async () => {
    if (!newProvinceId) {
      message.error(t('fullTruckLoad.provinceRequired'))
      return
    }
    if (await save(newProvinceId, newPercentage)) {
      setNewProvinceId(undefined)
      setNewPercentage(null)
    }
  }

  const handleDelete = async (provinceId) => {
    setBusyProvinceId(provinceId)
    try {
      await deleteRate({ variables: { provinceId } })
      message.success(t('deleted'))
      await refetch()
    } catch (mutationError) {
      message.error(mutationError.message)
    } finally {
      setBusyProvinceId(null)
    }
  }

  const startEdit = (rate) => {
    setEditingProvinceId(rate.province.id)
    setEditingPercentage(rate.percentage)
  }

  const columns = [
    {
      title: t('province'),
      key: 'province',
      render: (_value, rate) => <Link to={basketSizeDetailPath(rate.province.id)}>{rate.province.name}</Link>
    },
    {
      title: t('fullTruckLoad.percentage'),
      key: 'percentage',
      align: 'right',
      render: (_value, rate) => (editingProvinceId === rate.province.id
        ? (
          <PercentInput
            autoFocus
            onChange={setEditingPercentage}
            onPressEnter={handleSaveEdit}
            value={editingPercentage}
          />
          )
        : formatPercent(rate.percentage))
    },
    {
      title: t('updatedAt'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: value => (value ? dayjs(value).format(DATE_TIME_FORMAT) : '-')
    }
  ]

  if (canEdit) {
    columns.push({
      title: '',
      key: 'actions',
      render: (_value, rate) => {
        const provinceId = rate.province.id
        const busy = busyProvinceId === provinceId

        if (editingProvinceId === provinceId) {
          return (
            <Space>
              <Button icon={<CheckOutlined />} loading={busy} onClick={handleSaveEdit} size='small' type='primary'>
                {t('save')}
              </Button>
              <Button icon={<CloseOutlined />} onClick={() => setEditingProvinceId(null)} size='small'>
                {t('cancel')}
              </Button>
            </Space>
          )
        }

        return (
          <Space>
            <Button icon={<EditOutlined />} onClick={() => startEdit(rate)} size='small'>{t('edit')}</Button>
            <Popconfirm
              onConfirm={() => handleDelete(provinceId)}
              title={t('fullTruckLoad.deleteConfirm', { province: rate.province.name })}
            >
              <Button danger icon={<DeleteOutlined />} loading={busy} size='small'>{t('delete')}</Button>
            </Popconfirm>
          </Space>
        )
      }
    })
  }

  return (
    <React.Fragment>
      <Typography.Paragraph type='secondary'>{t('fullTruckLoad.description')}</Typography.Paragraph>

      {error && <Alert message={error.message} showIcon style={{ marginBottom: 16 }} type='error' />}

      {canEdit && (
        <Space style={{ marginBottom: 16 }} wrap>
          <Select
            filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
            onChange={setNewProvinceId}
            options={provincesWithoutRate.map(province => ({ value: province.id, label: province.name }))}
            placeholder={t('fullTruckLoad.selectProvince')}
            showSearch
            style={{ width: 240 }}
            value={newProvinceId}
          />
          <PercentInput onChange={setNewPercentage} placeholder='8' value={newPercentage} />
          <Button
            icon={<PlusOutlined />}
            loading={Boolean(newProvinceId) && busyProvinceId === newProvinceId}
            onClick={handleAdd}
            type='primary'
          >
            {t('fullTruckLoad.add')}
          </Button>
        </Space>
      )}

      <Table
        columns={columns}
        dataSource={rates}
        loading={loading}
        pagination={false}
        rowClassName={rate => (rate.province.id === highlightedProvinceId ? 'ant-table-row-selected' : '')}
        rowKey={rate => rate.province.id}
        scroll={{ x: 'max-content' }}
        size='small'
      />
    </React.Fragment>
  )
}

export default FullTruckLoadRatesTab
